import React from 'react';
import { Box, Typography, TextField, InputAdornment, IconButton, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import InstagramIcon from '../Dashboard/icons/InstagramIcon';
import FacebookIcon from '../icons/FacebookIcon';
import LinkedInIcon from '../icons/LinkedInIcon';
import YouTubeIcon from '../Dashboard/icons/YouTubeIcon';

const FooterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '80px',
  padding: '64px 40px',
  backgroundColor: '#1e293b', // dark slate background
  borderTop: '2px solid transparent',
  borderImage: 'linear-gradient(90deg, #22d3ee, #a5b4fc) 1',
  borderImageSlice: '1 0',
  width: '100%',
  maxWidth: '100%',
  margin: 0,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: '40px',
    padding: '40px 24px',
    alignItems: 'center'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '30px 16px',
    gap: '30px'
  }
}));

const LeftSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '40px',
  [theme.breakpoints.down('md')]: {
    alignItems: 'center',
    gap: '20px'

  }
}));


const Logo = styled('img')(({ theme }) => ({
  width: '143px',
  height: '48px'
}));

const CopyrightSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px'
}));

const CopyrightText = styled(Typography)(({ theme }) => ({
  width: '100%',
  maxWidth: '350px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  color: '#ffffff',
  letterSpacing: '0',
  lineHeight: '20px',
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
    lineHeight: '18px'
  }
}));

const SocialIcons = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  justifyContent: 'center'
}));

const SocialIconLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  color: '#94a3b8',
  textDecoration: 'none',
  transition: 'transform 0.3s ease, color 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    color: '#22d3ee'
  }
}));

const RightSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '30px',
  flex: 1,
  justifyContent: 'space-between',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: '30px',
    width: '100%'
  },
  [theme.breakpoints.down('sm')]: {
    gap: '24px'
  }
}));

const FooterColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '24px',
  [theme.breakpoints.down('md')]: {
    alignItems: 'center',
    width: '100%'
  },
  [theme.breakpoints.down('sm')]: {
    gap: '16px'
  }
}));

const ColumnTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '18px',
  color: '#ffffff',
  letterSpacing: '0',
  lineHeight: '24px',
  [theme.breakpoints.down('md')]: {
    textAlign: 'center'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px'
  }
}));

const ColumnLinks = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '12px',
  [theme.breakpoints.down('md')]: {
    alignItems: 'center'
  }
}));

const FooterLink = styled(Link)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  color: '#94a3b8',
  letterSpacing: '0',
  lineHeight: '20px',
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'color 0.3s ease',
  backgroundColor: 'transparent',
  border: 'none',
  padding: 0,
  textAlign: 'left',
  '&:hover': {
    color: '#22d3ee',
    textDecoration: 'underline'
  },
  [theme.breakpoints.down('md')]: {
    textAlign: 'center'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px'
  }
}));

const EmailSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '24px',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    alignItems: 'center',
    width: '100%'
  },
  [theme.breakpoints.down('sm')]: {
    gap: '16px'
  }
}));

const EmailInput = styled(TextField)(({ theme }) => ({
  width: '100%',
  maxWidth: '257px',
  height: '40px',
  '& .MuiOutlinedInput-root': {
    height: '40px',
    backgroundColor: '#0f172a',
    borderRadius: '8px',
    fontFamily: 'Inter, sans-serif',
    fontSize: '12px',
    '& fieldset': {
      border: '1px solid #334155'
    },
    '&:hover fieldset': {
      border: '1px solid #22d3ee'
    },
    '&.Mui-focused fieldset': {
      border: '1px solid #22d3ee'
    }
  },
  '& .MuiOutlinedInput-input': {
    padding: '9px 12px',
    color: '#ffffff',
    '&::placeholder': {
      color: '#94a3b8',
      opacity: 1
    }
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%'
  }
}));

const FooterSection = ({ onContactClick, onAboutClick }) => {
  const handleContactClick = (event) => {
    event.preventDefault();
    if (onContactClick) {
      onContactClick();
    }
  };

  const handleAboutClick = (event) => {
    event.preventDefault();
    if (onAboutClick) {
      onAboutClick();
    }
  };

  return (
    <FooterContainer>
      {/* Left Section */}
      <LeftSection>
        <Logo
          src="https://c.animaapp.com/mgbvruh0ta8bZ7/img/innovating-your-digital-future1-1-2.png"
          alt="Visibeen Logo"
        />

        <CopyrightSection>
          <CopyrightText>
            Copyright © 2025 e2e digitech pvt ltd.
          </CopyrightText>
          <CopyrightText>
            All rights reserved
          </CopyrightText>
        </CopyrightSection>

        <SocialIcons>
          <SocialIconLink href="https://www.instagram.com/visibeen/" target="_blank" rel="noopener noreferrer">
            <InstagramIcon width={32} height={32} color="#ffffff" />
          </SocialIconLink>
          <SocialIconLink href="https://www.facebook.com/profile.php?id=61566867428338" target="_blank" rel="noopener noreferrer">
            <FacebookIcon width={32} height={32} color="#ffffff" />
          </SocialIconLink>
          <SocialIconLink href="https://www.youtube.com/@Visibeen" target="_blank" rel="noopener noreferrer">
            <YouTubeIcon width={32} height={32} color="#ffffff" />
          </SocialIconLink>
          <SocialIconLink href="https://www.linkedin.com/company/visibeen/" target="_blank" rel="noopener noreferrer">
            <LinkedInIcon width={32} height={32} color="#ffffff" />
          </SocialIconLink>
        </SocialIcons>
      </LeftSection>

      {/* Right Section */}
      <RightSection>
        {/* Company Column */}
        <FooterColumn>
          <ColumnTitle>Company</ColumnTitle>
          <ColumnLinks>
            <FooterLink
              component="button"
              type="button"
              onClick={handleAboutClick}
            >
              About us
            </FooterLink>
            <FooterLink
              component="button"
              type="button"
              onClick={handleContactClick}
            >
              Contact us
            </FooterLink>
          </ColumnLinks>
        </FooterColumn>

        {/* Support Column */}
        <FooterColumn>
          <ColumnTitle>Support</ColumnTitle>
          <ColumnLinks>
            <FooterLink href="mailto:support@visibeen.com">support@visibeen.com</FooterLink>
            <FooterLink href="/terms-of-service">Terms of service</FooterLink>
            <FooterLink href="/privacy-policy">Privacy policy</FooterLink>
            <FooterLink href="/disclaimer">Disclaimer</FooterLink>
          </ColumnLinks>
        </FooterColumn>

        {/* Stay up to date Column */}
        <EmailSection>
          <ColumnTitle>Stay up to date</ColumnTitle>
          <EmailInput
            placeholder="Your email address"
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" sx={{
                    color: '#94a3b8',
                    '&:hover': {
                      color: '#22d3ee'
                    }
                  }}>
                    <SendIcon sx={{ fontSize: '18px' }} />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </EmailSection>
      </RightSection>
    </FooterContainer>
  );
};

export default FooterSection;